import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ExternalLink, Loader2, Target, Mail, Code2, Search,
  Share2, Megaphone, Headphones, Activity, Sparkles, LayoutGrid,
  MessageCircle, MessageSquare, Phone, Brain,
} from 'lucide-react';
import DashboardShell, { ghostChip } from '../components/DashboardShell';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
/** Direct backend origin for opening HTML preview tabs (avoids SPA catching /api when proxy is late). */
const BACKEND_ORIGIN = (API_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');

const SECTION_META = {
  leads: { label: 'CRM leads', icon: Target },
  campaigns: { label: 'Email campaigns', icon: Mail },
  sms: { label: 'SMS', icon: Phone },
  whatsapp: { label: 'WhatsApp', icon: MessageSquare },
  websites: { label: 'Websites / Builder', icon: Code2 },
  research: { label: 'Competitors / research', icon: Brain },
  seo: { label: 'SEO runs', icon: Search },
  social: { label: 'Social posts', icon: Share2 },
  ads: { label: 'Ads plans', icon: Megaphone },
  support: { label: 'Support drafts', icon: Headphones },
  actions: { label: 'Executed actions', icon: Activity },
  deliverables: { label: 'Deliverables', icon: Sparkles },
};

const card = 'rounded-2xl border border-white/12 bg-[#1c1914] shadow-[0_6px_0_0_rgba(0,0,0,0.35)]';
const cardYellow = 'rounded-2xl border-2 border-[#16130d] bg-[#fbbf0f] text-[#16130d] shadow-[0_6px_0_0_#16130d]';

function authHeaders() {
  const token = (localStorage.getItem('token') || '').trim();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const timeAgo = (iso) => {
  try {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
};

const unquoteLiteral = (raw) => {
  if (!raw || raw.length < 2) return raw || '';
  const q = raw[0];
  if ((q !== "'" && q !== '"') || raw[raw.length - 1] !== q) return raw;
  return raw.slice(1, -1).replace(new RegExp(`\\\\${q}`, 'g'), q).replace(/\\\\/g, '\\');
};

/** Prefer readable post text when API still returns stringified dict/list blobs. */
const formatOutputSummary = (summary) => {
  if (summary == null) return '';
  if (Array.isArray(summary)) {
    return summary.map(formatOutputSummary).find(Boolean) || '';
  }
  if (typeof summary === 'object') {
    const nested = summary.text || summary.content || summary.body
      || summary.caption || summary.message || summary.post;
    if (nested) return formatOutputSummary(nested);
    return '';
  }
  const text = String(summary).trim();
  if (!text) return '';
  const looksStructured = (
    (text.startsWith('{') && text.endsWith('}'))
    || (text.startsWith('[') && text.endsWith(']'))
  );
  if (!looksStructured) return text;
  try {
    return formatOutputSummary(JSON.parse(text));
  } catch {
    // Python-literal style: {'content': "..."} — pull quoted content/text fields.
    const m = text.match(
      /(?:'content'|"content"|'text'|"text"|'body'|"body"|'caption'|"caption")\s*:\s*('(?:\\'|[^'])*'|"(?:\\"|[^"])*")/,
    );
    if (m && m[1]) return unquoteLiteral(m[1]).trim();
    if (text.startsWith('[')) {
      const first = text.match(/'((?:\\'|[^']){8,})'|"((?:\\"|[^"]){8,})"/);
      if (first) return unquoteLiteral(first[0]);
    }
    return text;
  }
};

const openHref = (url, navigate) => {
  if (!url) return;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  // SiteBrain / API preview HTML must hit the backend (with token), never React Router.
  if (url.startsWith('/api/')) {
    const token = (localStorage.getItem('token') || '').trim();
    let target = `${BACKEND_ORIGIN}${url}`;
    if (token && !target.includes('token=')) {
      target += `${target.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
    }
    window.open(target, '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(url);
};

const OutputCard = ({ item, navigate }) => {
  const summary = formatOutputSummary(item.summary);
  const competitors = (item.extra && Array.isArray(item.extra.competitors))
    ? item.extra.competitors.filter((c) => c && c.name).slice(0, 5)
    : [];
  return (
    <button
      type="button"
      onClick={() => item.deep_link && openHref(item.deep_link, navigate)}
      className="w-full text-left rounded-xl border border-white/10 bg-[#0e0c0a] hover:border-[#fbbf0f]/50 px-3.5 py-3 transition"
      data-testid={`cofounder-output-item-${item.artifact_id || item.title}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">{item.title}</div>
          {summary ? (
            <div className="text-xs text-[#c4b8a5] mt-1 line-clamp-2 whitespace-pre-wrap font-medium">
              {summary}
            </div>
          ) : null}
          {competitors.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[11px] text-[#a89882] font-medium" data-testid="cofounder-output-competitors">
              {competitors.map((c) => (
                <li key={c.name} className="truncate">
                  • {c.name}{c.positioning ? ` — ${c.positioning}` : ''}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-[#a89882] font-medium">
            {item.status ? (
              <span className="rounded-full bg-[#fdf3ce] text-[#16130d] border border-[#16130d] px-1.5 py-0.5 font-bold">{item.status}</span>
            ) : null}
            {item.created_at ? <span>{timeAgo(item.created_at)}</span> : null}
          </div>
        </div>
        <ExternalLink className="h-3.5 w-3.5 text-[#fbbf0f] shrink-0 mt-0.5" />
      </div>
    </button>
  );
};

const WebsiteOutputCard = ({ item, navigate }) => {
  const extra = item.extra || {};
  const projectId = extra.project_id || '';
  const appId = extra.app_id || '';
  const chatId = extra.chat_id || '';
  const liveUrl = extra.live_url || '';
  const snagPath = () => {
    if (!appId) return '/snagbuilder';
    const qs = new URLSearchParams({ appId: String(appId) });
    if (chatId) qs.set('chatId', String(chatId));
    return `/snagbuilder?${qs.toString()}`;
  };
  const snagFallback = (url) => {
    if (!url) return '';
    if (String(url).includes('/app-builder')) return snagPath();
    return url;
  };
  const builderUrl = snagFallback(
    extra.builder_url
    || (appId ? snagPath() : '')
    || (item.deep_link && String(item.deep_link).includes('snagbuilder') ? item.deep_link : '')
    || (item.deep_link && String(item.deep_link).includes('app-builder') ? snagPath() : '')
    || '/snagbuilder',
  );
  const previewUrl = snagFallback(
    extra.preview_url
    || (appId ? snagPath() : '')
    || (projectId ? `/api/sitebrain/projects/${projectId}/preview` : ''),
  );
  const editUrl = extra.edit_url || '';
  const status = item.status || (liveUrl ? 'published' : 'draft');
  const publishReady = Boolean(extra.publish_ready) || (Boolean(previewUrl) && !liveUrl);
  const missingAppId = !appId && Boolean(builderUrl) && !String(builderUrl).includes('appId=');
  // In-product preview: Snag Builder (iframe), not raw /api HTML navigation.
  const openPreviewInProduct = () => {
    if (builderUrl) {
      navigate(builderUrl);
      return;
    }
    openHref(previewUrl, navigate);
  };

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#0e0c0a] px-3.5 py-3 space-y-2.5"
      data-testid={`cofounder-output-website-${item.artifact_id || item.title}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-bold text-white truncate">{item.title}</div>
          {item.summary && (
            <div className="text-xs text-[#c4b8a5] mt-1 line-clamp-2 whitespace-pre-wrap font-medium">{item.summary}</div>
          )}
        </div>
        <span className="rounded-full bg-[#fdf3ce] text-[#16130d] border border-[#16130d] px-1.5 py-0.5 text-[11px] font-bold shrink-0">
          {status}
        </span>
      </div>

      <div className="space-y-1.5 text-[11px] font-medium">
        {liveUrl ? (
          <button
            type="button"
            onClick={() => openHref(liveUrl, navigate)}
            className="flex items-center gap-1.5 w-full text-left text-emerald-300 hover:text-emerald-200 truncate"
            data-testid="cofounder-website-live-url"
          >
            <span className="text-[#a89882] shrink-0">Live</span>
            <span className="truncate underline underline-offset-2">{liveUrl}</span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </button>
        ) : (
          <p className="text-[#a89882]">Live — not published yet</p>
        )}
        {builderUrl || previewUrl ? (
          <button
            type="button"
            onClick={openPreviewInProduct}
            className="flex items-center gap-1.5 w-full text-left text-[#fbbf0f] hover:text-[#ffe08a] truncate"
            data-testid="cofounder-website-preview-url"
          >
            <span className="text-[#a89882] shrink-0">Preview</span>
            <span className="truncate underline underline-offset-2">
              {builderUrl || previewUrl}
            </span>
            <ExternalLink className="h-3 w-3 shrink-0" />
          </button>
        ) : (
          <p className="text-[#a89882]">Preview — open Builder to continue</p>
        )}
        {missingAppId ? (
          <p className="text-[10px] text-amber-200/90" data-testid="cofounder-website-missing-appid">
            No Snag app linked yet — Builder opens home. Re-run Launch Brand to create + deep-link this site.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 pt-0.5">
        {liveUrl && (
          <button type="button" onClick={() => openHref(liveUrl, navigate)} className={`${ghostChip} !py-1 !text-[11px]`}>
            Open live
          </button>
        )}
        {(builderUrl || previewUrl) && (
          <button
            type="button"
            onClick={openPreviewInProduct}
            className={`${ghostChip} !py-1 !text-[11px]`}
            data-testid="cofounder-website-open-preview"
          >
            Open preview
          </button>
        )}
        {editUrl && (
          <button type="button" onClick={() => navigate(editUrl)} className={`${ghostChip} !py-1 !text-[11px]`}>
            Edit site
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate(builderUrl)}
          className={`${ghostChip} !py-1 !text-[11px]`}
          data-testid="cofounder-website-builder-cta"
        >
          {appId ? 'Open in Snag Builder' : 'Open Snag Builder'}
        </button>
        {publishReady && !liveUrl && (
          <button
            type="button"
            onClick={() => navigate(editUrl || builderUrl)}
            className="inline-flex items-center gap-1 rounded-full bg-[#fbbf0f] text-[#16130d] border-2 border-[#16130d] px-2.5 py-1 text-[11px] font-extrabold"
            data-testid="cofounder-website-publish-cta"
          >
            Approve / publish
          </button>
        )}
      </div>
      {item.created_at ? (
        <p className="text-[10px] text-[#a89882] font-medium">{timeAgo(item.created_at)}</p>
      ) : null}
    </div>
  );
};

const SectionPanel = ({ sectionKey, items, navigate }) => {
  const meta = SECTION_META[sectionKey] || SECTION_META.actions;
  const Icon = meta.icon;
  const useWebsiteCard = sectionKey === 'websites';
  return (
    <section
      className={`${card} p-4 flex flex-col ${items.length ? 'min-h-[220px]' : 'min-h-0'}`}
      data-testid={`cofounder-output-section-${sectionKey}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-extrabold flex items-center gap-2 text-[#fbbf0f]">
          <span className="w-7 h-7 rounded-lg bg-[#fbbf0f] border-2 border-[#16130d] text-[#16130d] inline-flex items-center justify-center">
            <Icon className="h-3.5 w-3.5" />
          </span>
          {meta.label}
        </h2>
        <span className="text-[11px] font-extrabold text-[#16130d] bg-[#fbbf0f] border border-[#16130d] rounded-full px-2 py-0.5">
          {items.length}
        </span>
      </div>
      <div className="space-y-2 flex-1 overflow-y-auto max-h-[340px]">
        {items.length === 0 ? (
          <p className="text-xs text-[#a89882] py-2 text-center font-medium">No items yet</p>
        ) : (
          items.map((item) => (
            useWebsiteCard || item.kind === 'website' ? (
              <WebsiteOutputCard key={`${sectionKey}-${item.artifact_id || item.title}-${item.created_at}`} item={item} navigate={navigate} />
            ) : (
              <OutputCard key={`${sectionKey}-${item.artifact_id || item.title}-${item.created_at}`} item={item} navigate={navigate} />
            )
          ))
        )}
      </div>
    </section>
  );
};

const AICofounderOutputs = ({ user }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const companyId = params.get('company_id') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [sessions, setSessions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      if (sessionId) qs.set('session_id', sessionId);
      if (companyId && !sessionId) qs.set('company_id', companyId);
      const [outRes, sessRes] = await Promise.all([
        fetch(`${API_URL}/api/cofounder/outputs?${qs.toString()}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/cofounder/sessions?limit=40`, { headers: authHeaders() }),
      ]);
      if (!outRes.ok) {
        const err = await outRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to load outputs');
      }
      setPayload(await outRes.json());
      const sessData = await sessRes.json();
      setSessions(sessData.sessions || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, companyId]);

  useEffect(() => { load(); }, [load]);

  const outputs = payload?.outputs;
  const sections = outputs?.sections || {};
  const hubLinks = outputs?.hub_links || [];
  const sectionOrder = useMemo(
    () => [
      'leads', 'campaigns', 'sms', 'whatsapp', 'websites', 'research', 'seo',
      'social', 'ads', 'support', 'actions', 'deliverables',
    ],
    [],
  );

  const onSelectSession = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set('session_id', id);
    else next.delete('session_id');
    setParams(next);
  };

  return (
    <DashboardShell
      user={user}
      fullscreen
      title="Brand outputs"
      subtitle={payload?.company?.name || 'AI Co-founder results hub'}
      activeNav="ai-cofounder"
      backPath="/ai-cofounder"
      headerExtras={(
        <button
          type="button"
          onClick={() => navigate('/ai-cofounder', { state: { resumeWorkspace: true } })}
          className={ghostChip}
          data-testid="cofounder-outputs-back-control"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      )}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-5" data-testid="cofounder-outputs-page">
        <div className={`${cardYellow} p-5 flex flex-col sm:flex-row sm:items-end justify-between gap-3`}>
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <LayoutGrid className="h-5 w-5" />
              <span className="text-xs font-extrabold uppercase tracking-wide">Results hub</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-normal" data-testid="cofounder-outputs-title">
              {payload?.company?.name || 'Company'} results
            </h1>
            <p className="text-sm font-medium text-[#3a3327] mt-1">
              All modules stay visible — zero means nothing for this run yet. Click a chip to open that product area.
            </p>
            <button
              type="button"
              onClick={() => navigate('/ai-cofounder', { state: { resumeWorkspace: true, openChat: true } })}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-extrabold text-[#16130d] underline underline-offset-2 hover:opacity-80"
              data-testid="cofounder-outputs-chat-link"
            >
              <MessageCircle className="h-3.5 w-3.5" />
              Chat with your Co-founder →
            </button>
          </div>
          <select
            value={sessionId || payload?.session?.id || ''}
            onChange={(e) => onSelectSession(e.target.value)}
            className="rounded-full bg-[#16130d] text-white border-2 border-[#16130d] px-3 py-2 text-sm font-semibold outline-none"
            data-testid="cofounder-outputs-session-select"
          >
            <option value="">Current run</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {(s.label || s.id).slice(0, 48)}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-[#fbbf0f]" />
          </div>
        ) : error ? (
          <div className={`${card} border-red-400/40 p-6 text-sm text-red-200 font-medium`} data-testid="cofounder-outputs-error">
            {error}
            <button type="button" onClick={() => navigate('/ai-cofounder')} className="block mt-3 text-[#fbbf0f] font-extrabold underline">
              Go create a company first
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#c4b8a5] font-medium" data-testid="cofounder-outputs-session-meta">
              <span className="text-white font-extrabold">{outputs?.total || 0}</span> items
              {(outputs?.counts?.leads || 0) > 0 ? ` · ${outputs.counts.leads} leads` : ''}
              {(outputs?.counts?.campaigns || 0) > 0 ? ` · ${outputs.counts.campaigns} campaigns` : ''}
              {(outputs?.counts?.websites || 0) > 0 ? ` · ${outputs.counts.websites} sites` : ''}
            </p>

            <div className="flex flex-wrap gap-2" data-testid="cofounder-outputs-hub-links">
              {hubLinks.map((link) => (
                <button
                  key={link.path + link.label}
                  type="button"
                  onClick={() => navigate(link.path)}
                  className={ghostChip}
                  data-testid={`cofounder-outputs-hub-${link.label}`}
                >
                  {link.label}
                  {link.count != null ? (
                    <span className={link.count > 0 ? 'text-[#fbbf0f] font-extrabold' : 'text-[#a89882] font-extrabold'}>
                      {link.count}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {sectionOrder.map((key) => (
                <SectionPanel
                  key={key}
                  sectionKey={key}
                  items={sections[key] || []}
                  navigate={navigate}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default AICofounderOutputs;
